<template>
  <div class="plans-page admin-page">
    <header class="admin-page__header">
      <div class="admin-page__title">
        <h2>套餐管理</h2>
        <p>管理付费套餐、席位额度与周期</p>
      </div>
      <a-button type="primary" icon="plus" @click="openCreate">新建套餐</a-button>
    </header>

    <a-card :bordered="false" class="admin-card">
      <a-table
        :columns="columns"
        :data-source="rows"
        :rowKey="r => r.id"
        :loading="loading"
        :pagination="false"
        size="middle"
        class="admin-table"
      >
        <template slot="enabled" slot-scope="text">
          <span class="status-pill" :class="text ? 'is-on' : 'is-off'">
            <i></i>{{ text ? '启用' : '停用' }}
          </span>
        </template>
        <template slot="apiq" slot-scope="text">
          <span v-if="text === null || text === undefined" class="quota-default">默认</span>
          <span v-else>{{ text }}</span>
        </template>
        <template slot="action" slot-scope="_, record">
          <a class="row-action" @click="openEdit(record)">编辑</a>
          <a-divider type="vertical" />
          <a-popconfirm title="确定删除该套餐?" @confirm="onDelete(record)">
            <a class="row-action is-danger">删除</a>
          </a-popconfirm>
        </template>
      </a-table>
    </a-card>

    <a-modal
      :visible="modalOpen"
      :title="modalTitle"
      @ok="onSave"
      @cancel="modalOpen = false"
      :destroy-on-close="true"
      :width="560"
    >
      <a-form-model :model="form" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="代码 (code)">
          <a-input
            v-model="form.code"
            :disabled="editing"
            placeholder="trial / starter / pro / max / ..."
          />
        </a-form-model-item>
        <a-form-model-item label="名称">
          <a-input v-model="form.name" />
        </a-form-model-item>
        <a-form-model-item label="单价 (U)">
          <a-input-number v-model="form.price_u" :min="0" :step="0.01" style="width:100%" />
        </a-form-model-item>
        <a-form-model-item label="席位数 slot_total">
          <a-input-number v-model="form.slot_total" :min="0" style="width:100%" />
        </a-form-model-item>
        <a-form-model-item label="BYO 额度">
          <a-input-number v-model="form.byo_slot_total" :min="0" style="width:100%" />
        </a-form-model-item>
        <a-form-model-item label="每日 API 额度">
          <a-input-number
            v-model="form.api_daily_quota"
            :min="0"
            :step="1000"
            style="width:100%"
            placeholder="留空 = 全局默认"
          />
          <div class="form-hint">留空 = 使用全局默认（约 10000/天）；填数字 = 该套餐每日固定额度（如 10000 / 50000 / 100000）。</div>
        </a-form-model-item>
        <a-form-model-item label="周期 days">
          <a-input-number v-model="form.days" :min="1" style="width:100%" />
        </a-form-model-item>
        <a-form-model-item label="排序">
          <a-input-number v-model="form.sort_order" style="width:100%" />
        </a-form-model-item>
        <a-form-model-item label="启用">
          <a-switch v-model="form.enabled" />
        </a-form-model-item>
        <a-form-model-item label="描述">
          <a-input v-model="form.description" type="textarea" :rows="3" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { listPlans, savePlan, deletePlan } from '@/api/plans';

export default {
  data() {
    return {
      loading: false,
      rows: [],
      modalOpen: false,
      editing: false,
      form: this.emptyForm(),
      columns: [
        { title: 'Code', dataIndex: 'code', width: 100 },
        { title: '名称', dataIndex: 'name', width: 120 },
        { title: '单价 (U)', dataIndex: 'price_u', width: 100 },
        { title: 'Slots', dataIndex: 'slot_total', width: 80 },
        { title: 'BYO', dataIndex: 'byo_slot_total', width: 80 },
        { title: 'API额度/天', dataIndex: 'api_daily_quota', scopedSlots: { customRender: 'apiq' }, width: 100 },
        { title: '天数', dataIndex: 'days', width: 80 },
        { title: '排序', dataIndex: 'sort_order', width: 80 },
        {
          title: '状态',
          dataIndex: 'enabled',
          scopedSlots: { customRender: 'enabled' },
          width: 90,
        },
        { title: '操作', scopedSlots: { customRender: 'action' }, width: 140 },
      ],
    };
  },
  computed: {
    modalTitle() {
      return this.editing ? '编辑套餐' : '新建套餐';
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    emptyForm() {
      return {
        code: '',
        name: '',
        price_u: 0,
        slot_total: 1,
        byo_slot_total: 0,
        api_daily_quota: null,
        days: 30,
        sort_order: 0,
        enabled: true,
        description: '',
      };
    },
    refresh() {
      this.loading = true;
      listPlans()
        .then(res => {
          this.rows = (res.data || []).map(r => ({ ...r, enabled: !!r.enabled }));
        })
        .catch(e => {
          this.$message.error((e && e.data && e.data.message) || '加载失败');
        })
        .finally(() => (this.loading = false));
    },
    openCreate() {
      this.form = this.emptyForm();
      this.editing = false;
      this.modalOpen = true;
    },
    openEdit(r) {
      this.form = { ...r, enabled: !!r.enabled };
      this.editing = true;
      this.modalOpen = true;
    },
    onSave() {
      savePlan({
        ...this.form,
        enabled: this.form.enabled ? 1 : 0,
      })
        .then(() => {
          this.$message.success('保存成功');
          this.modalOpen = false;
          this.refresh();
        })
        .catch(e => this.$message.error((e && e.data && e.data.message) || '保存失败'));
    },
    onDelete(r) {
      deletePlan(r.code)
        .then(() => {
          this.$message.success('删除成功');
          this.refresh();
        })
        .catch(e => this.$message.error((e && e.data && e.data.message) || '删除失败'));
    },
  },
};
</script>

<style scoped>
.form-hint {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #9aa3b2;
}
.quota-default {
  color: #9aa3b2;
}
</style>